import { Button, Stack } from '@mui/joy';
import { X } from '@phosphor-icons/react';
import { UseFieldArrayReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useAuthUser } from 'auth/hooks';
import { LocationFormField } from 'contacts/elements';
import { contactGroupService } from 'contacts/services';
import { FormField } from 'core/components';
import { NON_EMPTY } from 'core/constants';
import { InputType } from 'core/enums';
import { useFormContext } from 'core/states';
import { IPricingProfileAccessForm } from 'pricing/components';
import { Color, Direction, Variant } from 'theme/enums';

export interface IProps {
  customers: UseFieldArrayReturn<IPricingProfileAccessForm, 'customers'>;
}

export const CustomersField = ({ customers }: IProps) => {
  const { t } = useTranslation();
  const { control, watch, errors, setValue } = useFormContext();
  const { user } = useAuthUser();

  const isAlternateVersionEnabled = Boolean(user?.alternateVersionEnabled);

  const onRemove = (index: number) => {
    if (customers.fields.length === 1) {
      setValue('allCustomers', true);
    }

    customers.remove(index);
  };

  return (
    <>
      {customers.fields.map((customerField, index) => {
        const customerValue = watch(`customers.${index}`);

        return (
          <Stack
            key={customerField.id}
            direction={Direction.ROW}
            spacing={1}
            alignItems="flex-start"
          >
            <FormField
              name={`customers.${index}.customerContactGroup`}
              type={InputType.AUTOCOMPLETE}
              wrapperStyle={{ flex: 2 }}
              inputProps={{
                control,
                error: errors.customers?.[index]?.customerContactGroup,
                placeholder: t('placeholderSelectCustomer'),
                getOptions: contactGroupService.getManyAutocomplete,
                requestPayload: {
                  filters: {
                    isCustomer: '"1"',
                  },
                },
                disableClearable: true,
                onChange: () => {
                  setValue(`customers.${index}.customerLocations`, []);
                },
              }}
            />

            <LocationFormField
              key={`locations-${customerValue.customerContactGroup?.id || 'none'}`}
              name={`customers.${index}.customerLocations`}
              label=""
              placeholder={
                customerValue.customerLocations?.length > 0 ? '' : t('placeholderSelectLocation')
              }
              requestPayload={{
                filters: {
                  contactGroupId: customerValue.customerContactGroup?.id,
                  ...(isAlternateVersionEnabled && {
                    isBillTo: 1,
                    accountsReceivableId: NON_EMPTY,
                  }),
                },
              }}
              disabled={!customerValue.customerContactGroup}
              groupOptionsByContactGroup={false}
              required={true}
              disableClearable={true}
              hasCreateOption={false}
              multiple={true}
              wrapperStyle={{ flex: 3 }}
            />

            <Button variant={Variant.PLAIN} color={Color.DANGER} onClick={() => onRemove(index)}>
              <X />
            </Button>
          </Stack>
        );
      })}
    </>
  );
};
